import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { DashboardBoard } from '@/components/intelligence/dashboard/DashboardBoard';
import { buildDefaultForecastLayout } from '@/features/intelligence/dashboard/defaults';
import type { ForecastSettings } from '@/features/intelligence/dashboard/evaluate';
import {
  FORECAST_HISTORY_OPTIONS,
  FORECAST_HORIZON_OPTIONS,
  parseForecastHistory,
  parseForecastHorizon,
  resolveForecastWindow,
  type ForecastHistory,
  type ForecastHorizon,
} from '@/features/intelligence/dashboard/range';

const DEFAULT_HORIZON: ForecastHorizon = 6;
const DEFAULT_HISTORY: ForecastHistory = 12;

export function ForecastPage() {
  const [params, setParams] = useSearchParams();
  const horizon = parseForecastHorizon(params.get('horizon'));
  const history = parseForecastHistory(params.get('history'));
  const window = useMemo(() => resolveForecastWindow(history), [history]);
  const forecast = useMemo<ForecastSettings>(() => ({ horizon, history }), [horizon, history]);

  const setParam = (key: 'horizon' | 'history', value: number, fallback: number) =>
    setParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        if (value === fallback) nextParams.delete(key);
        else nextParams.set(key, String(value));
        return nextParams;
      },
      { replace: true },
    );

  const controls = (
    <>
      <Select value={String(history)} onValueChange={(value) => setParam('history', Number(value), DEFAULT_HISTORY)}>
        <SelectTrigger className="h-8 w-[160px] text-xs" aria-label="History shown">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FORECAST_HISTORY_OPTIONS.map((option) => (
            <SelectItem key={option.id} value={String(option.id)}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={String(horizon)} onValueChange={(value) => setParam('horizon', Number(value), DEFAULT_HORIZON)}>
        <SelectTrigger className="h-8 w-[160px] text-xs" aria-label="Forecast horizon">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FORECAST_HORIZON_OPTIONS.map((option) => (
            <SelectItem key={option.id} value={String(option.id)}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  );

  return (
    <DashboardBoard
      page="forecast"
      title="Forecast"
      eyebrow="Intelligence"
      window={window}
      forecast={forecast}
      buildDefault={buildDefaultForecastLayout}
      controls={controls}
      featuredKinds={['forecast']}
      subtitle={`${history} months of history · projecting ${horizon} months ahead`}
    />
  );
}
